import React, { useState, useEffect } from 'react';
import {
  BarcodeScanner as MlKitBarcodeScanner,
  BarcodeFormat,
} from '@capacitor-mlkit/barcode-scanning';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Camera, X, Package, Loader, Edit, CheckCircle, Zap } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import EstimationModal from './EstimationModal';
import FoodEducationSheetModal from './FoodEducationSheetModal';
import { computeProductRating } from '../services/productRatingService';
import { getFoodEducationSheet, inferNutritionReference } from '../services/foodEducationService';
import { trackAnalyticsEvent } from '../services/analyticsService';
import freemiumService from '../services/freemiumService';

// Keep explicit rear-camera probe helpers in source for static regression tests.
function probeEnvironmentCameraId(cameras = []) {
  if (!Array.isArray(cameras) || cameras.length === 0) {
    return null;
  }
  const environmentCandidate = cameras.find((camera) => /back|rear|environment|camera\s*0/i.test(String(camera?.label || '')));
  return environmentCandidate?.id || null;
}

function buildMobileStartConstraints() {
  return [
    { facingMode: { exact: 'environment' } },
    { facingMode: 'environment' },
  ];
}

function buildDesktopStartConstraints() {
  return [{ facingMode: 'user' }];
}

function BarcodeScanner({ onItemAdded, onClose, startInManualMode = false }) {
  const [scanning, setScanning] = useState(false);
  const [manualInput, setManualInput] = useState(startInManualMode);
  const [barcode, setBarcode] = useState('');
  const [productInfo, setProductInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showCorrectionModal, setShowCorrectionModal] = useState(false);
  const [showQuantityModal, setShowQuantityModal] = useState(false);
  const [showEstimationModal, setShowEstimationModal] = useState(false);
  const [showMagasinModal, setShowMagasinModal] = useState(false);
  const [showFoodSheet, setShowFoodSheet] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [unit, setUnit] = useState('pièces');
  const [magasinSelected, setMagasinSelected] = useState('');
  const [prixReel, setPrixReel] = useState('');
  const [prixEstimation, setPrixEstimation] = useState(null);

  // États pour la correction manuelle
  const [correctedProduct, setCorrectedProduct] = useState({
    nom: '',
    marque: '',
    quantity_raw: '',
    categorie: 'alimentation'
  });

  useEffect(() => {
    if (startInManualMode) {
      setManualInput(true);
      setScanning(false);
    }
  }, [startInManualMode]);
  const scanWithMlKit = async () => {
    try {
      setScanning(true);
      setLoading(true);

      const supported = await MlKitBarcodeScanner.isSupported();
      if (!supported.supported) {
        handleScanError(new Error('MLKIT_NOT_SUPPORTED'));
        return;
      }

      const permission = await MlKitBarcodeScanner.requestPermissions();
      if (permission.camera !== 'granted') {
        handleScanError(new Error('NotAllowedError'));
        return;
      }

      const result = await MlKitBarcodeScanner.scan({
        formats: [
          BarcodeFormat.Ean13,
          BarcodeFormat.Ean8,
          BarcodeFormat.UpcA,
          BarcodeFormat.UpcE,
          BarcodeFormat.Code128,
          BarcodeFormat.Code39,
          BarcodeFormat.Code93,
          BarcodeFormat.Itf,
          BarcodeFormat.QrCode,
        ],
        autoZoom: true,
      });

      const scannedCode = result?.barcodes?.[0]?.rawValue;
      if (!scannedCode) {
        toast.warning('Aucun code-barres detecte');
        return;
      }

      if (navigator.vibrate) navigator.vibrate(200);
      toast.success(`📱 Code scanne : ${scannedCode}`);
      await processBarcode(scannedCode);
    } catch (error) {
      console.error('Erreur ML Kit scanner:', error);
      handleScanError(error);
    } finally {
      setScanning(false);
      setLoading(false);
    }
  };

  const startScanner = () => {
    if (scanning || loading) {
      return;
    }
    toast.info('Démarrage du scanner...');
    setManualInput(false);
    scanWithMlKit();
  };

  const handleScanError = (error) => {
    const msg = typeof error === 'string' ? error : (error?.message || String(error));
    const errorName = typeof error === 'object' ? (error?.name || '') : '';
    const normalizedMessage = String(msg || '').toLowerCase();
    let errorTitle = 'Erreur de démarrage';
    let errorMessage = 'Impossible de démarrer le scanner';

    if (msg.includes('NotAllowedError') || errorName === 'NotAllowedError' || normalizedMessage.includes('permission denied')) {
      errorTitle = 'Permission refusée';
      errorMessage = 'Permission caméra refusée. Autorisez la caméra dans les paramètres Android pour utiliser le scanner.';
    } else if (msg.includes('WEBVIEW_CAMERA_BLOCKED') || errorName === 'SecurityError' || normalizedMessage.includes('permission policy') || normalizedMessage.includes('only secure origins are allowed')) {
      errorTitle = 'WebView non autorisee';
      errorMessage = "La WebView ne permet pas l'accès caméra. Vérifiez les permissions Android et la configuration Capacitor.";
    } else if (msg.includes('CAMERA_API_UNAVAILABLE')) {
      errorTitle = 'API camera indisponible';
      errorMessage = "La caméra vidéo n'est pas disponible dans cet environnement. Utilisez la saisie manuelle.";
    } else if (msg.includes('NotFoundError')) {
      errorTitle = 'Caméra introuvable';
      errorMessage = 'Aucune caméra détectée sur cet appareil.';
    } else if (msg.includes('NotReadableError')) {
      errorTitle = 'Caméra occupée';
      errorMessage = 'La caméra est déjà utilisée par une autre application. Fermez les autres applications puis réessayez.';
    } else if (msg.includes('MLKIT_NOT_SUPPORTED')) {
      errorTitle = 'Scanner indisponible';
      errorMessage = 'Scanner natif non supporte sur cet appareil.';
    }

    toast.error(`${errorTitle}: ${errorMessage}`);
    setScanning(false);
    setManualInput(true);
  };

  const stopScanner = async () => {
    try {
      await MlKitBarcodeScanner.stopScan();
    } catch {}
    setScanning(false);
  };

  const handleManualBarcode = async () => {
    if (!barcode.trim()) {
      toast.error('Veuillez entrer un code-barres');
      return;
    }
    
    await processBarcode(barcode.trim());
  };

  const processBarcode = async (code) => {
    setLoading(true);
    setBarcode(code); // Sauvegarder le code pour fallback manuel
    trackAnalyticsEvent('scan_performed', { barcode: code });
    
    try {
      const barcodeAccess = await freemiumService.checkBarcodeScanAllowed();
      if (!barcodeAccess?.allowed) {
        toast.error(barcodeAccess?.message || 'Scan code-barres indisponible pour le moment');
        return;
      }

      if (barcodeAccess?.ad_required) {
        toast.info('Mode free: une publicite peut s afficher pendant le scan code-barres.');
      }

      const response = await axios.post('/items/scan-barcode', {
        barcode: code
      });

      await freemiumService.recordUsage('barcode_scans');

      if (response.data.success) {
        const product = response.data.product;
        setProductInfo(product);
        trackAnalyticsEvent('scan_result_loaded', {
          barcode: code,
          source: product?.source || 'unknown',
          has_nutri_score: Boolean(product?.nutri_score || product?.nutriscore_grade),
        });
        
        // Initialiser les données de correction
        setCorrectedProduct({
          nom: product.nom || '',
          marque: product.marque || '',
          quantity_raw: product.quantity_raw || '',
          categorie: product.categorie || 'alimentation'
        });
        
        // Afficher modal de correction
        setShowCorrectionModal(true);
        
      } else {
        // Produit non trouvé - Proposer saisie manuelle enrichie
        handleProductNotFound(code, response.data.message);
      }
    } catch (error) {
      console.error('Erreur scan:', error);

      const detail = error?.response?.data?.detail;
      const paywallError = typeof detail === 'string' && detail.startsWith('PAYWALL:');

      if (paywallError) {
        const cleanMessage = detail.replace('PAYWALL:', '').trim();
        toast.error(`${cleanMessage} - bascule vers saisie manuelle.`);
        handleProductNotFound(code, cleanMessage || 'Mode free');
        return;
      }

      if (error?.response?.status === 402 || error?.response?.status === 403) {
        toast.error(detail || 'Acces limite temporairement pour ce scan');
        return;
      }
      
      // Erreur réseau ou serveur
      toast.error('Erreur de connexion. Vérifiez votre réseau.', {
        duration: 4000,
        action: {
          label: 'Réessayer',
          onClick: () => processBarcode(code)
        }
      });
      
      // Fallback vers saisie manuelle après 2s
      setTimeout(() => {
        handleProductNotFound(code, 'Erreur de connexion');
      }, 2000);
    } finally {
      setLoading(false);
    }
  };

  const handleProductNotFound = (code, message) => {
    toast.warning(`Produit non trouvé (${code})`, {
      duration: 5000,
      description: 'Le code-barres est valide mais le produit n\'est pas dans notre base. Vous pouvez l\'ajouter manuellement.'
    });
    
    // Pré-remplir avec le code-barres et passer en mode manuel enrichi
    setBarcode(code);
    setManualInput(true);
    
    // Créer un produit fallback
    setProductInfo({
      barcode: code,
      nom: `Produit scanné (${code})`,
      marque: '',
      quantity_raw: '',
      quantite_detectee: null,
      unite_detectee: null,
      categorie: 'alimentation',
      source: 'manuel',
      needs_completion: true
    });
    
    setCorrectedProduct({
      nom: `Produit scanné (${code})`,
      marque: '',
      quantity_raw: '',
      categorie: 'alimentation'
    });
    
    // Afficher directement le modal de correction pour saisie manuelle enrichie
    setTimeout(() => {
      setShowCorrectionModal(true);
    }, 1000);
  };

  const handleConfirmProduct = async () => {
    setShowCorrectionModal(false);
    
    const updatedProduct = {
      ...productInfo,
      nom: correctedProduct.nom,
      marque: correctedProduct.marque,
      quantity_raw: correctedProduct.quantity_raw,
      categorie: correctedProduct.categorie
    };
    setProductInfo(updatedProduct);
    
    // Si l'utilisateur a modifié les données OU si c'est un produit non trouvé (fallback/manuel)
    const hasChanges = 
      correctedProduct.nom !== productInfo.nom ||
      correctedProduct.marque !== productInfo.marque ||
      correctedProduct.quantity_raw !== productInfo.quantity_raw ||
      correctedProduct.categorie !== productInfo.categorie;
    
    const isNewProduct = productInfo.needs_completion || 
                         productInfo.source === 'fallback' || 
                         productInfo.source === 'manuel';
    
    if (hasChanges || isNewProduct) {
      try {
        await axios.post('/items/enrich-product', {
          barcode: productInfo.barcode,
          corrected_data: correctedProduct
        });
        
        if (isNewProduct) {
          toast.success('✅ Nouveau produit enregistré ! Il sera reconnu au prochain scan');
        } else {
          toast.success('✅ Vos corrections ont été enregistrées');
        }
      } catch (error) {
        console.error('Erreur enrichissement:', error);
        toast.error('Erreur lors de l\'enregistrement');
      }
    }
    
    if (!updatedProduct.quantite_detectee) {
      setShowQuantityModal(true);
      setUnit('pièces');
    } else {
      addToStock(updatedProduct, 1, updatedProduct.unite_detectee || 'pièces');
    }
  };

  const addToStock = async (product, qty, selectedUnit) => {
    try {
      const response = await axios.post('/items/add-scanned', {
        product: product,
        quantite: qty,
        unite: selectedUnit
      });

      if (response.data.success) {
        toast.success(response.data.message);
        
        // Stocker les infos pour les modals suivants
        setProductInfo({
          ...product,
          item_id: response.data.item_id,
          movement_id: response.data.movement_id || null
        });
        setPrixEstimation(response.data.prix_estimation || { prix_estimes: {} });
        
        // Afficher d'abord le modal d'estimation
        setShowEstimationModal(true);
      } else {
        toast.error('Erreur lors de l\'ajout au stock');
      }
    } catch (error) {
      console.error('Erreur ajout stock:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Erreur lors de l\'ajout au stock';
      toast.error(`Erreur: ${errorMsg}`);
    }
  };

  const handleEstimationValidate = async (estimationData) => {
    try {
      await axios.post('/items/configure-estimation', {
        item_id: productInfo.item_id,
        consommation_jour: estimationData.consommation_jour,
        unite: estimationData.unite
      });
      
      toast.success(`Estimation enregistrée: ${estimationData.duree_estimee_jours} jours`);
      
      setShowMagasinModal(true);
    } catch (error) {
      console.error('Erreur sauvegarde estimation:', error.response?.data || error.message || error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde de l\'estimation');
      setShowMagasinModal(true);
    }
  };

  const handleQuantitySubmit = async () => {
    if (!quantity || quantity <= 0) {
      toast.error('Veuillez entrer une quantité valide');
      return;
    }

    await addToStock(productInfo, quantity, unit);
  };

  // Base de codes-barres de test
  const testBarcodes = [
    { code: '3017620425035', name: 'Nutella 400g' },
    { code: '3033710074617', name: 'Coca Cola 1.5L' },
    { code: '3228857000013', name: 'Evian 1.5L' },
    { code: '3274080005003', name: 'Cristaline 1.5L' },
  ];

  const quickTestBarcode = (code, name) => {
    toast.info(`Test : ${name}`);
    processBarcode(code);
  };

  const scoreData = computeProductRating(productInfo || {});
  const foodSheet = getFoodEducationSheet(productInfo?.nom || 'Aliment', inferNutritionReference(productInfo || {}));
  const backendNutritionScore = productInfo?.nutrition_score || null;
  const backendAnalysis = productInfo?.analysis || null;
  const nutrition = productInfo?.nutrition || {};
  const additives = Array.isArray(productInfo?.additives) ? productInfo.additives : [];
  const analysisPositives = Array.isArray(backendAnalysis?.positives) ? backendAnalysis.positives : scoreData.pointsForts;
  const analysisWarnings = Array.isArray(backendAnalysis?.warnings) ? backendAnalysis.warnings : scoreData.pointsSurveillance;
  const displayScoreStatus = productInfo?.score_status || backendAnalysis?.score_status || 'unavailable';
  const displayProductType = productInfo?.product_type || backendAnalysis?.product_type || 'unknown';
  const hasActionableNutritionScore = displayProductType === 'food' && ['official', 'calculated', 'estimated'].includes(displayScoreStatus);
  const score100 = hasActionableNutritionScore
    ? (backendNutritionScore?.score_100 ?? Math.round((scoreData.score || 0) * 10))
    : null;
  const score20 = hasActionableNutritionScore
    ? (backendNutritionScore?.score_20 ?? Number(((scoreData.score || 0) * 2).toFixed(1)))
    : null;
  const scoreLabel = score100 == null
    ? 'indisponible'
    : (backendNutritionScore?.label || (score100 >= 80 ? 'excellent' : score100 >= 65 ? 'bon' : score100 >= 45 ? 'moyen' : 'mauvais'));
  const scoreBadge = String(productInfo?.nutri_score || productInfo?.nutriscore_grade || backendAnalysis?.score || scoreData.nutriScore || 'N/D').toUpperCase();
  const productTypeLabel = {
    food: 'Alimentaire',
    beauty: 'Beauté',
    hygiene: 'Hygiène',
    pharmacy: 'Soin / Pharmacie',
    household: 'Ménage',
    unknown: 'Inconnu',
  };
  const statusLabel = {
    official: 'Officiel',
    calculated: 'Calculé',
    estimated: 'Estimé',
    unavailable: 'Indisponible',
  };
  const scoreColorClasses = {
    excellent: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    bon: 'bg-lime-100 text-lime-800 border-lime-200',
    moyen: 'bg-amber-100 text-amber-800 border-amber-200',
    mauvais: 'bg-rose-100 text-rose-800 border-rose-200',
    indisponible: 'bg-slate-100 text-slate-800 border-slate-200',
  };
  const additiveRiskClasses = {
    eleve: 'bg-red-100 text-red-700 border-red-200',
    modere: 'bg-orange-100 text-orange-700 border-orange-200',
    faible: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    inconnu: 'bg-slate-100 text-slate-700 border-slate-200',
  };

  const openFoodSheet = () => {
    setShowFoodSheet(true);
    trackAnalyticsEvent('food_sheet_viewed_from_scan', {
      barcode: productInfo?.barcode,
      product_name: productInfo?.nom,
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
      <Card className={`w-full mx-2 overflow-y-auto ${scanning ? 'max-w-5xl max-h-[96vh]' : 'max-w-md max-h-[90vh]'}`}>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center space-x-2">
              <Camera className="w-5 h-5 text-blue-600" />
              <span>Scanner un Article</span>
            </CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="h-8 w-8 p-0"
            >
              <X size={16} />
            </Button>
          </div>
        </CardHeader>

        <CardContent>
          {!scanning && !manualInput && !loading && !showCorrectionModal && !showQuantityModal && (
            <div className="space-y-4">
              <div className="bg-blue-50 p-3 rounded-lg border border-blue-200 mb-4">
                <p className="text-sm text-blue-700 font-medium mb-2">📱 Scanner avec votre caméra</p>
                <p className="text-xs text-blue-600">
                  ✅ Tous formats supportés : EAN-13/8, Code-128/39, UPC, ITF, DataMatrix, QR
                </p>
                <p className="text-xs text-blue-600 mt-1">
                  📦 Produits complexes (boîtes, packs) supportés
                </p>
              </div>
              
              <div className="grid grid-cols-1 gap-3">
                <Button
                  onClick={startScanner}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                >
                  <Camera size={16} className="mr-2" />
                  📷 Scanner automatiquement
                </Button>
                
                <Button
                  variant="outline"
                  onClick={() => setManualInput(true)}
                  className="w-full"
                >
                  <Package size={16} className="mr-2" />
                  ⌨️ Saisie manuelle directe
                </Button>
              </div>

              {/* Tests rapides */}
              <div className="border-t pt-3 mt-4">
                <p className="text-xs text-gray-500 mb-2">🧪 Tests rapides :</p>
                <div className="grid grid-cols-2 gap-2">
                  {testBarcodes.map((item, index) => (
                    <Button
                      key={index}
                      variant="outline"
                      size="sm"
                      onClick={() => quickTestBarcode(item.code, item.name)}
                      className="text-xs p-2"
                    >
                      {item.name}
                    </Button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Zone de scanner natif */}
          {scanning && (
            <div className="space-y-4">
              <div className="bg-green-100 p-2 rounded text-sm text-green-800 mb-2">
                🎥 <strong>Scanner natif ML Kit actif</strong> - Ouverture de la caméra en cours
              </div>
              
              <div className="bg-yellow-50 p-3 rounded-lg border border-yellow-200">
                <p className="text-sm text-yellow-800 font-medium mb-2">
                  💡 Conseils pour un scan réussi :
                </p>
                <ul className="text-xs text-yellow-700 space-y-1">
                  <li>• <strong>Positionnez</strong> le code-barres dans le cadre de scan natif</li>
                  <li>• <strong>Tenez stable</strong> votre téléphone 2-3 secondes</li>
                  <li>• <strong>Bon éclairage</strong> - évitez les reflets et ombres</li>
                  <li>• <strong>Distance</strong> - ni trop proche, ni trop loin (15-20cm)</li>
                  <li>• <strong>Formats supportés</strong> : EAN-13/8, Code-128/39, UPC, ITF, QR</li>
                  <li>• La détection est <strong>automatique</strong></li>
                </ul>
              </div>
              
              <div className="bg-blue-50 p-2 rounded-lg border border-blue-200">
                <p className="text-xs text-blue-700 text-center">
                  ⚠️ <strong>Problème de scan ?</strong> Cliquez sur "Saisie manuelle" pour entrer le code directement
                </p>
              </div>
              
              <div className="grid grid-cols-3 gap-2">
                <Button
                  onClick={stopScanner}
                  variant="outline"
                  className="w-full"
                >
                  ❌ Arrêter
                </Button>
                <Button
                  variant="default"
                  onClick={scanWithMlKit}
                  className="w-full"
                >
                  🔁 Relancer
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => {
                    stopScanner();
                    setManualInput(true);
                  }}
                  className="w-full"
                >
                  ⌨️ Saisie manuelle
                </Button>
              </div>
            </div>
          )}

          {/* Saisie manuelle */}
          {manualInput && (
            <div className="space-y-4">
              <div className="bg-yellow-50 p-3 rounded-lg border border-yellow-200">
                <p className="text-sm text-yellow-800">
                  ⌨️ <strong>Saisie du code-barres</strong>
                </p>
                <p className="text-xs text-yellow-700 mt-1">
                  Tapez les 13 chiffres sous les barres
                </p>
              </div>

              <div className="space-y-2">
                <Label>Code-barres (13 chiffres)</Label>
                <Input
                  type="text"
                  placeholder="Ex: 3017620425035"
                  value={barcode}
                  onChange={(e) => setBarcode(e.target.value.replace(/\D/g, ''))}
                  onKeyPress={(e) => e.key === 'Enter' && handleManualBarcode()}
                  maxLength={13}
                  autoFocus
                  className="text-lg text-center font-mono"
                />
                {barcode && barcode.length !== 13 && (
                  <p className="text-xs text-orange-600">
                    ⚠️ Les codes-barres font généralement 13 chiffres
                  </p>
                )}
              </div>
              
              <div className="flex space-x-2">
                <Button
                  onClick={handleManualBarcode}
                  disabled={!barcode.trim() || loading}
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                >
                  {loading ? <Loader size={16} className="animate-spin mr-2" /> : <Zap size={16} className="mr-2" />}
                  🔍 Rechercher
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setManualInput(false);
                    setBarcode('');
                  }}
                >
                  Retour
                </Button>
              </div>

              {/* Codes test */}
              <div className="border-t pt-3">
                <p className="text-xs text-gray-500 mb-2">📋 Codes de test :</p>
                <div className="space-y-1">
                  {testBarcodes.slice(0, 3).map((item, index) => (
                    <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded text-xs">
                      <span className="font-mono">{item.code}</span>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setBarcode(item.code)}
                        className="h-6 px-2 text-xs"
                      >
                        Copier
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Loading */}
          {loading && (
            <div className="text-center py-8">
              <Loader size={32} className="animate-spin mx-auto mb-4 text-blue-600" />
              <p className="text-sm text-gray-600">Recherche du produit...</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal de correction/validation */}
      {showCorrectionModal && productInfo && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-60 p-4">
          <Card className="w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <CardTitle className="text-lg flex items-center space-x-2">
                <Edit className="w-5 h-5 text-blue-600" />
                <span>Vérifier et Corriger</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="max-h-[70vh] overflow-y-auto">
              <div className="space-y-4">
                <div className={`p-3 rounded-lg border ${
                  productInfo.needs_completion ? 'bg-orange-50 border-orange-200' : 
                  productInfo.source === 'cache' || productInfo.source === 'user_enrichment' ? 'bg-green-50 border-green-200' :
                  'bg-blue-50 border-blue-200'
                }`}>
                  <p className={`text-sm font-medium mb-2 ${
                    productInfo.needs_completion ? 'text-orange-900' : 
                    productInfo.source === 'cache' || productInfo.source === 'user_enrichment' ? 'text-green-900' :
                    'text-blue-900'
                  }`}>
                    {productInfo.needs_completion ? '⚠️ Produit non trouvé - Saisie manuelle' : 
                     productInfo.source === 'cache' || productInfo.source === 'user_enrichment' ? '✅ Produit reconnu - Données enregistrées' :
                     '📦 Produit détecté - Vérifiez les informations'}
                  </p>
                  <div className={`text-xs space-y-1 ${
                    productInfo.needs_completion ? 'text-orange-700' : 
                    productInfo.source === 'cache' || productInfo.source === 'user_enrichment' ? 'text-green-700' :
                    'text-blue-700'
                  }`}>
                    <p>Code-barres : <strong>{productInfo.barcode}</strong></p>
                    {!productInfo.needs_completion && (
                      <p>Source : <strong>{
                        productInfo.source === 'cache' ? '💾 Base locale (scan précédent)' :
                        productInfo.source === 'user_enrichment' ? '💾 Vos données enregistrées' :
                        productInfo.source === 'openfoodfacts' ? '🌐 OpenFoodFacts' : 
                        productInfo.source === 'openbeautyfacts' ? '🌐 OpenBeautyFacts' : 
                        productInfo.source === 'openproductsfacts' ? '🌐 OpenProductsFacts' : 
                        productInfo.source === 'local_fr' ? '📚 Référentiel FR' : 
                        productInfo.source
                      }</strong></p>
                    )}
                    {productInfo.needs_completion && (
                      <p className="font-medium">Veuillez compléter les informations ci-dessous</p>
                    )}
                    {(productInfo.source === 'cache' || productInfo.source === 'user_enrichment') && (
                      <p className="font-medium text-green-800 mt-2">
                        ℹ️ Ces informations proviennent de votre dernier scan. Vous pouvez les modifier si nécessaire.
                      </p>
                    )}
                  </div>
                </div>

                <div className="rounded-lg border border-cyan-200 bg-cyan-50 p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-semibold text-cyan-900">Rapport produit apres scan</p>
                    <div className="flex items-center gap-2 text-xs flex-wrap justify-end">
                      <span className="rounded-full bg-white px-2 py-1 text-cyan-800 border border-cyan-100">Type: {productTypeLabel[displayProductType] || 'Inconnu'}</span>
                      <span className="rounded-full bg-white px-2 py-1 text-cyan-800 border border-cyan-100">Statut score: {statusLabel[displayScoreStatus] || 'Indisponible'}</span>
                      <span className="rounded-full bg-white px-2 py-1 text-cyan-800 border border-cyan-100">Nutri-Score: {scoreBadge}</span>
                      <span className="rounded-full bg-white px-2 py-1 text-cyan-800 border border-cyan-100">NOVA: {productInfo?.nova_group || 'N/D'}</span>
                      <span className="rounded-full bg-white px-2 py-1 text-cyan-800 border border-cyan-100">Eco: {productInfo?.ecoscore_grade || 'N/D'}</span>
                    </div>
                  </div>

                  <div className="rounded-xl border border-white/80 bg-gradient-to-r from-white to-cyan-100 p-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs uppercase tracking-wide text-slate-500">Score nutritionnel</p>
                        {score100 == null ? (
                          <p className="text-sm font-semibold text-slate-700">Non applicable / indisponible</p>
                        ) : (
                          <>
                            <p className="text-3xl font-bold text-slate-900">{score100}<span className="text-sm font-medium text-slate-500">/100</span></p>
                            <p className="text-xs text-slate-500">({score20}/20)</p>
                          </>
                        )}
                      </div>
                      <div className={`rounded-full px-3 py-1 text-sm font-semibold border ${scoreColorClasses[scoreLabel] || scoreColorClasses.moyen}`}>
                        {scoreLabel.toUpperCase()}
                      </div>
                    </div>
                    <p className="text-xs text-slate-700 mt-2">{backendAnalysis?.score_note || backendNutritionScore?.recommendation || scoreData.verdict}</p>
                  </div>

                  {productInfo?.image_url && (
                    <div className="overflow-hidden rounded-md border border-cyan-100 bg-white">
                      <img src={productInfo.image_url} alt={productInfo.nom || 'Produit'} className="h-36 w-full object-contain" loading="lazy" />
                    </div>
                  )}

                  <p className="text-xs text-cyan-800"><strong>Resume rapide:</strong> {backendAnalysis?.summary || scoreData.summary}</p>
                  <p className="text-xs text-cyan-800"><strong>Verdict global:</strong> {scoreData.verdict}</p>

                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 text-xs">
                    <div className="rounded-md bg-white p-2">
                      <p className="font-semibold text-emerald-700">Points forts</p>
                      <p className="text-slate-700">{analysisPositives.length ? analysisPositives.join(' • ') : 'Donnees insuffisantes'}</p>
                    </div>
                    <div className="rounded-md bg-white p-2">
                      <p className="font-semibold text-amber-700">Points a surveiller</p>
                      <p className="text-slate-700">{analysisWarnings.length ? analysisWarnings.join(' • ') : 'Aucun signal majeur detecte'}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2 text-[11px] text-slate-700">
                    <div>Marque: {productInfo?.marque || 'N/D'}</div>
                    <div>Transformation: {productInfo?.transformation_level || 'N/D'}</div>
                    <div>Source: {productInfo?.source || 'N/D'}</div>
                    <div>Additifs: {productInfo?.additives_count ?? additives.length ?? 0}</div>
                  </div>

                  <div className="rounded-md bg-white p-3 border border-cyan-100">
                    <p className="text-xs font-semibold text-slate-800 mb-2">Nutrition detaillee (pour 100g)</p>
                    <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[11px] text-slate-700">
                      <div>Calories: {nutrition.calories_100g ?? 'N/D'} kcal</div>
                      <div>Proteines: {nutrition.proteins_100g ?? 'N/D'} g</div>
                      <div>Glucides: {nutrition.carbs_100g ?? 'N/D'} g</div>
                      <div>Sucres: {nutrition.sugars_100g ?? 'N/D'} g</div>
                      <div>Sel: {nutrition.salt_100g ?? 'N/D'} g</div>
                      <div>Lipides: {nutrition.fat_100g ?? 'N/D'} g</div>
                      <div>Acides gras satures: {nutrition.saturated_fat_100g ?? 'N/D'} g</div>
                      <div>Fibres: {nutrition.fiber_100g ?? 'N/D'} g</div>
                    </div>
                  </div>

                  <p className="text-[11px] text-cyan-900"><strong>Ingredients:</strong> {productInfo?.ingredients_text || 'Informations non disponibles'}</p>
                  <p className="text-[11px] text-cyan-900"><strong>Allergenes:</strong> {Array.isArray(productInfo?.allergens_list) ? productInfo.allergens_list.join(', ') : (Array.isArray(productInfo?.allergens) ? productInfo.allergens.join(', ') : (productInfo?.allergens || 'Informations non disponibles'))}</p>

                  {additives.length > 0 && (
                    <div className="rounded-md bg-white p-3 border border-cyan-100">
                      <p className="text-xs font-semibold text-slate-800 mb-2">Additifs detectes</p>
                      <div className="space-y-2 max-h-40 overflow-y-auto pr-1">
                        {additives.map((additive, idx) => (
                          <div key={`${additive.code}-${idx}`} className="rounded-md border border-slate-100 p-2">
                            <div className="flex items-center justify-between gap-2">
                              <p className="text-[11px] font-medium text-slate-800">{additive.code} - {additive.name}</p>
                              <span className={`text-[10px] px-2 py-0.5 rounded-full border ${additiveRiskClasses[additive.risk_level] || additiveRiskClasses.inconnu}`}>
                                {String(additive.risk_level || 'inconnu').toUpperCase()}
                              </span>
                            </div>
                            <p className="text-[10px] text-slate-600 mt-1">{additive.description || 'Description indisponible'}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {additives.length === 0 && (
                    <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-[11px] text-emerald-800">
                      Aucun additif detecte ou information indisponible pour ce produit.
                    </div>
                  )}

                  <div className="flex flex-wrap gap-2 pt-1">
                    <Button type="button" variant="outline" className="text-xs" onClick={openFoodSheet}>
                      Voir le detail
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      className="text-xs"
                      onClick={() => toast.info('Alternatives plus interessantes: fonctionnalite en cours d enrichissement.')}
                    >
                      Alternatives plus interessantes
                    </Button>
                  </div>
                </div>
                
                {productInfo.estimation_auto && (
                  <div className="bg-green-50 p-3 rounded-lg border border-green-200">
                    <p className="text-sm font-medium text-green-900 mb-1">
                      🧠 Estimation automatique appliquée
                    </p>
                    <p className="text-xs text-green-700">
                      {productInfo.quantite_estimee ? 
                        `Quantité détectée: ${productInfo.quantite_detectee} ${productInfo.unite_detectee}` :
                        `Quantité par défaut appliquée`
                      }
                    </p>
                  </div>
                )}
                
                <div className="space-y-3">
                  <div className="space-y-2">
                    <Label>Nom du produit *</Label>
                    <Input
                      value={correctedProduct.nom}
                      onChange={(e) => setCorrectedProduct({...correctedProduct, nom: e.target.value})}
                      placeholder="Ex: Eau Cristaline"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Marque</Label>
                    <Input
                      value={correctedProduct.marque}
                      onChange={(e) => setCorrectedProduct({...correctedProduct, marque: e.target.value})}
                      placeholder="Ex: Cristaline"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Quantité emballage</Label>
                    <Input
                      value={correctedProduct.quantity_raw}
                      onChange={(e) => setCorrectedProduct({...correctedProduct, quantity_raw: e.target.value})}
                      placeholder="Ex: 1.5L, 500g, 6 pièces, 6x33cL"
                    />
                    <p className="text-xs text-gray-500">
                      💡 Exemples : "1kg", "250mL", "Boîte 400g", "Pack 6x33cL"
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label>Catégorie</Label>
                    <Select 
                      value={correctedProduct.categorie} 
                      onValueChange={(value) => setCorrectedProduct({...correctedProduct, categorie: value})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="alimentation">Alimentation</SelectItem>
                        <SelectItem value="boisson">Boisson</SelectItem>
                        <SelectItem value="hygiène">Hygiène</SelectItem>
                        <SelectItem value="ménage">Ménage</SelectItem>
                        <SelectItem value="autre">Autre</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="bg-yellow-50 p-3 rounded-lg border border-yellow-200">
                  <p className="text-xs text-yellow-800 mb-2">
                    💡 <strong>Conseils de vérification :</strong>
                  </p>
                  <ul className="text-xs text-yellow-700 space-y-1 ml-4">
                    <li>• Vérifiez que le <strong>nom</strong> correspond au produit scanné</li>
                    <li>• Confirmez la <strong>quantité</strong> indiquée sur l'emballage</li>
                    <li>• Ajustez la <strong>catégorie</strong> si nécessaire</li>
                    <li>• Vos corrections enrichissent la base de données</li>
                  </ul>
                </div>
                
                <div className="flex space-x-2">
                  <Button
                    onClick={handleConfirmProduct}
                    disabled={!correctedProduct.nom.trim()}
                    className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                  >
                    <CheckCircle size={16} className="mr-2" />
                    Confirmer
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowCorrectionModal(false);
                      setProductInfo(null);
                    }}
                  >
                    Annuler
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Modal de quantité */}
      {showQuantityModal && productInfo && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-60 p-4">
          <Card className="w-full max-w-sm">
            <CardHeader>
              <CardTitle className="text-lg">Quantité du produit</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="bg-blue-50 p-3 rounded-lg">
                  <p className="text-sm font-medium text-blue-900">
                    {productInfo.nom}
                  </p>
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Quantité</Label>
                    <Input
                      type="number"
                      min="0"
                      step="1"
                      value={quantity}
                      onChange={(e) => {
                        const val = e.target.value;
                        setQuantity(val === '' ? '' : parseFloat(val));
                      }}
                      onBlur={(e) => {
                        // Si vide au blur, mettre 1 par défaut
                        if (e.target.value === '' || parseFloat(e.target.value) < 0) {
                          setQuantity(1);
                        }
                      }}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Unité</Label>
                    <Select value={unit} onValueChange={setUnit}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pièces">pièces</SelectItem>
                        <SelectItem value="g">g</SelectItem>
                        <SelectItem value="kg">kg</SelectItem>
                        <SelectItem value="mL">mL</SelectItem>
                        <SelectItem value="L">L</SelectItem>
                        <SelectItem value="boîtes">boîtes</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="flex space-x-2">
                  <Button
                    onClick={handleQuantitySubmit}
                    className="flex-1 bg-green-600 hover:bg-green-700"
                  >
                    Ajouter au stock
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowQuantityModal(false);
                      setProductInfo(null);
                    }}
                  >
                    Annuler
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Modal d'estimation de consommation */}
      <EstimationModal
        isOpen={showEstimationModal}
        onClose={() => setShowEstimationModal(false)}
        productInfo={productInfo}
        onValidate={handleEstimationValidate}
      />

      {/* Modal de sélection magasin et prix */}
      {showMagasinModal && productInfo && prixEstimation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-60 p-4">
          <Card className="w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <CardTitle className="text-lg">🏬 Où avez-vous acheté ce produit ?</CardTitle>
            </CardHeader>
            <CardContent className="max-h-[70vh] overflow-y-auto">
              <div className="space-y-4">
                <div className="bg-blue-50 p-3 rounded-lg">
                  <p className="text-sm font-medium text-blue-900">
                    📦 {productInfo.nom}
                  </p>
                </div>

                {prixEstimation.prix_estimes && Object.keys(prixEstimation.prix_estimes).length > 0 ? (
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-2">
                      💰 Comparaison des prix
                    </p>
                    <div className="border rounded-lg overflow-hidden max-h-[250px] overflow-y-auto">
                      {Object.entries(prixEstimation.prix_estimes)
                        .sort(([, a], [, b]) => a - b)
                        .map(([enseigne, prix], index) => (
                          <div
                            key={enseigne}
                            className={`flex items-center justify-between p-2 cursor-pointer hover:bg-gray-50 ${
                              index === 0 ? 'bg-green-50 border-l-4 border-green-500' : 'border-b'
                            } ${magasinSelected === enseigne ? 'bg-blue-50 border-l-4 border-blue-500' : ''}`}
                            onClick={() => setMagasinSelected(enseigne)}
                          >
                            <div className="flex items-center space-x-2">
                              <input
                                type="radio"
                                name="magasin"
                                value={enseigne}
                                checked={magasinSelected === enseigne}
                                onChange={() => setMagasinSelected(enseigne)}
                                className="w-4 h-4"
                              />
                              <span className="font-medium text-gray-800">{enseigne}</span>
                              {index === 0 && (
                                <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                                  ✅ Meilleur
                                </span>
                              )}
                            </div>
                            <span className="text-lg font-bold text-gray-900">{prix.toFixed(2)} €</span>
                          </div>
                        ))}
                    </div>

                    <div className="mt-2 bg-yellow-50 p-2 rounded-lg border border-yellow-200">
                      <div className="text-xs text-yellow-800">
                        <p>📊 Moyen: {prixEstimation.prix_moyen?.toFixed(2)} €
                        {prixEstimation.economie_possible > 0 && (
                          <span> | 💰 Économie: {prixEstimation.economie_possible.toFixed(2)} € chez {prixEstimation.magasin_moins_cher}</span>
                        )}
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-2">
                      🏬 Sélectionnez votre magasin
                    </p>
                    <div className="border rounded-lg overflow-hidden max-h-[300px] overflow-y-auto">
                      {['Carrefour', 'Lidl', 'Auchan', 'Intermarché', 'Leclerc', 'Casino', 'Monoprix', 'Autre'].map((enseigne) => (
                        <div
                          key={enseigne}
                          className={`flex items-center justify-between p-2 cursor-pointer hover:bg-gray-50 border-b last:border-b-0 ${
                            magasinSelected === enseigne ? 'bg-blue-50 border-l-4 border-blue-500' : ''
                          }`}
                          onClick={() => setMagasinSelected(enseigne)}
                        >
                          <div className="flex items-center space-x-2">
                            <input
                              type="radio"
                              name="magasin"
                              value={enseigne}
                              checked={magasinSelected === enseigne}
                              onChange={() => setMagasinSelected(enseigne)}
                              className="w-4 h-4"
                            />
                            <span className="font-medium text-gray-800">{enseigne}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <Label>💵 Prix payé (obligatoire)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="Ex: 3.70"
                    value={prixReel}
                    onChange={(e) => setPrixReel(e.target.value)}
                  />
                  <p className="text-xs text-gray-500">
                    Le prix réel est obligatoire pour enregistrer cet achat dans l'historique et le budget.
                  </p>
                </div>

                {prixReel && parseFloat(prixReel) > 0 && (
                  <div className="bg-green-50 p-3 rounded-lg border border-green-200">
                    <p className="text-sm text-green-800">
                      ✅ Prix : <strong>{parseFloat(prixReel).toFixed(2)} €</strong>
                    </p>
                    <p className="text-xs text-green-700 mt-1">
                      Cet achat sera enregistré dans votre historique
                    </p>
                  </div>
                )}

                <div className="flex space-x-2">
                  <Button
                    onClick={async () => {
                      if (!prixReel || parseFloat(prixReel) <= 0) {
                        toast.error('Le prix est obligatoire pour valider cet achat');
                        return;
                      }

                      if (magasinSelected && productInfo.item_id) {
                        try {
                          const requestData = {
                            item_id: productInfo.item_id,
                            magasin: magasinSelected,
                            prix_reel: prixReel ? parseFloat(prixReel) : null,
                            movement_id: productInfo.movement_id || null
                          };
                          console.log('📤 Sending update-magasin-prix request:', requestData);
                          
                          await axios.post('/items/update-magasin-prix', requestData);
                          
                          if (prixReel && parseFloat(prixReel) > 0) {
                            toast.success(`✅ Prix enregistré : ${parseFloat(prixReel).toFixed(2)}€ chez ${magasinSelected}`);
                          } else {
                            toast.success(`Magasin enregistré : ${magasinSelected}`);
                          }
                        } catch (error) {
                          console.error('Erreur mise à jour magasin:', error);
                          toast.error('Erreur lors de l\'enregistrement');
                        }
                      }
                      setShowMagasinModal(false);
                      onItemAdded && onItemAdded();
                      onClose();
                    }}
                    disabled={!magasinSelected || !prixReel || parseFloat(prixReel) <= 0}
                    className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                  >
                    <CheckCircle size={16} className="mr-2" />
                    {prixReel && parseFloat(prixReel) > 0 ? 'Enregistrer' : 'Confirmer'}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <FoodEducationSheetModal open={showFoodSheet} sheet={foodSheet} onClose={() => setShowFoodSheet(false)} />
    </div>
  );
}

export default BarcodeScanner;
